import { BUSINESS_TOOL_SUB_SECTIONS } from 'constants/businessTool';

import {
  EQUIPMENT_NAMES,
  EQUIPMENT_SECTION_NAMES,
} from 'components/BusinessToolForm/Sections/BusinessOperations/Equipment/utils/form';
import {
  LOCATION_AND_PREMISES_NAMES,
  PREMISES_INFORMATION_NAMES,
} from 'components/BusinessToolForm/Sections/BusinessOperations/LocationAndPremises/utils/form';
import { ASSETS_NAMES } from 'components/BusinessToolForm/Sections/BusinessOperations/OtherAssets/utils/form';
import {
  KEY_SUPPLIERS_CHARACTERIZE,
  PARTNERS_NAMES,
} from 'components/BusinessToolForm/Sections/BusinessOperations/PartnersSection/utils/form';
import { PRODUCTION_NAMES } from 'components/BusinessToolForm/Sections/BusinessOperations/ProductionSection/utils/form';
import { QUALITY_NAMES } from 'components/BusinessToolForm/Sections/BusinessOperations/QualityControl/utils/form';
import {
  STAGES_NAMES,
  TECHNOLOGICAL_PROCESS_NAMES,
} from 'components/BusinessToolForm/Sections/BusinessOperations/TechnologicalProcess/utils/form';
import { WORKING_RESOURCES_NAMES } from 'components/BusinessToolForm/Sections/BusinessOperations/WorkingResources/utils/form';

import { ul } from '../helpers';

const PREMISES_INFORMATION_INSTRUCTIONS = {
  [PREMISES_INFORMATION_NAMES.FILE]: {
    name: '',
    instruction:
      'Incluya una fotografía de alta calidad de los edificios o las instalaciones. Asegúrese de que la imagen esté bien iluminada y tomada de manera profesional.',
    exampleOne: '',
    exampleTwo: '',
  },

  [PREMISES_INFORMATION_NAMES.ADDRESS]: {
    name: 'Dirección',
    instruction:
      'Proporcione la dirección completa del edificio o las instalaciones. Incluya la calle, el número del edificio, la ciudad, el estado o provincia, el código postal y el país. Asegúrese de que la dirección sea precisa y esté completa para evitar cualquier ambigüedad.',
    exampleOne: 'Ton Ave, Des Moines, IA 50313, US',
    exampleTwo: '03 Sylvan Ave, Mountain View, CA 94041',
  },

  [PREMISES_INFORMATION_NAMES.AREA]: {
    name: 'Superficie',
    instruction:
      'Indique la superficie total del edificio o las instalaciones en pies cuadrados, metros cuadrados, hectáreas u otras unidades de medida.',
    exampleOne: '1,2 hectáreas',
    exampleTwo: '750 ft²',
  },

  [PREMISES_INFORMATION_NAMES.TERMS_OF_USE]: {
    name: 'Condiciones de uso',
    instruction:
      'Indique si las instalaciones son de su propiedad o están alquiladas. Si son alquiladas, especifique el importe del alquiler. Si son de su propiedad, indique el valor actual del inmueble y las condiciones de adquisición.',
    exampleOne: 'El edificio fue adquirido en enero de 2020 por 10 millones de USD.',
    exampleTwo: 'Las instalaciones se alquilan por 5.000 USD al mes.',
  },

  [PREMISES_INFORMATION_NAMES.CURRENT_COST]: {
    name: 'Valor actual',
    instruction: 'Indique el valor de mercado actual del edificio o las instalaciones.',
    exampleOne: '',
    exampleTwo: '',
  },

  [PREMISES_INFORMATION_NAMES.PURPOSE]: {
    name: 'Uso',
    instruction:
      'Describa el uso previsto del edificio o las instalaciones. Especifique si se destinan a actividades comerciales, residenciales, industriales u otros fines. Describa también las actividades que se desarrollan en ellas, como trabajo de oficina, producción, comercio minorista o prestación de servicios.',
    exampleOne: 'Instalación de producción.',
    exampleTwo: 'Oficinas ubicadas en un centro de negocios.',
  },
};

const LOCATION_AND_PREMISES_INSTRUCTIONS = {
  [LOCATION_AND_PREMISES_NAMES.LOCATION]: {
    name: 'Ubicación de las instalaciones',
    instruction:
      'Proporcione una descripción general de las ubicaciones de su empresa o de sus instalaciones de producción. Destaque las principales zonas geográficas, ciudades o países donde se encuentran. Si corresponde, indique también el tamaño y la capacidad de cada instalación para ofrecer una visión general de su infraestructura operativa.',
    exampleOne:
      'La planta de producción de la empresa está estratégicamente ubicada en el corazón del Medio Oeste, cerca de importantes centros agrícolas como Des Moines, Iowa. Esta ubicación privilegiada garantiza la proximidad al mercado objetivo y un acceso eficiente a las redes de transporte, optimizando la distribución.',
    exampleTwo:
      'La sede central está estratégicamente situada en el corazón de Silicon Valley, lo que permite acceder a un amplio ecosistema de talento tecnológico e innovación. Además, la empresa cuenta con oficinas regionales en centros estratégicos como Londres y Singapur para atender eficazmente a diferentes mercados.',
  },

  [LOCATION_AND_PREMISES_NAMES.PREMISES_INFORMATION]: PREMISES_INFORMATION_INSTRUCTIONS,

  [LOCATION_AND_PREMISES_NAMES.QUALITIES]: {
    name: 'Ventajas y desventajas de la ubicación',
    instruction:
      'Especifique aspectos como:' +
      ul([
        'el coste del alquiler y de los servicios públicos,',
        'la proximidad a los clientes objetivo,',
        'la proximidad a los proveedores,',
        'la carga fiscal de la zona,',
        'la calidad de las conexiones de transporte,',
        'las variaciones demográficas estacionales u otros factores relevantes.',
      ]),
    exampleOne:
      'La ubicación actual de la empresa implica elevados costes de alquiler y servicios públicos, pero ofrece proximidad tanto a los clientes objetivo como a los proveedores. La zona cuenta con una baja carga fiscal, excelentes conexiones de transporte y una demografía estacional estable.',
    exampleTwo:
      'La ubicación actual de la empresa ofrece costes competitivos de alquiler y servicios públicos, proximidad a clientes de importantes regiones agrícolas y fácil acceso a proveedores. No obstante, la carga fiscal es moderada y las variaciones demográficas estacionales pueden afectar a la disponibilidad de mano de obra.',
  },

  [LOCATION_AND_PREMISES_NAMES.FUTURE_PLANS]: {
    name: 'Expansión futura',
    instruction:
      'Describa en detalle los planes relacionados con nuevas ubicaciones o instalaciones de producción. Explique si la empresa prevé construir nuevos edificios, alquilar instalaciones existentes o establecer alianzas para compartir infraestructuras.',
    exampleOne:
      'La empresa planea expandirse hacia el sureste para acceder a nuevos mercados agrícolas en crecimiento. Inicialmente alquilará instalaciones existentes para optimizar los costes, aunque también evaluará alianzas estratégicas para compartir infraestructuras. Esta expansión permitirá ampliar la presencia en el mercado y aprovechar el incremento de la demanda regional.',
    exampleTwo:
      'La empresa estudia expandirse hacia mercados emergentes como América Latina y el Sudeste Asiático mediante la apertura de oficinas regionales para reforzar su presencia local. El alquiler de instalaciones existentes y las alianzas para compartir infraestructuras constituyen opciones viables que aportan flexibilidad y eficiencia económica a la estrategia de expansión.',
  },
};
const MAIN_EQUIPMENT_INSTRUCTIONS = {
  [EQUIPMENT_SECTION_NAMES.FILE]: {
    name: '',
    instruction:
      'Incluya una fotografía de alta calidad del equipo. Asegúrese de que la imagen esté bien iluminada y tomada de manera profesional.',
    exampleOne: '',
    exampleTwo: '',
  },

  [EQUIPMENT_SECTION_NAMES.NAME]: {
    name: 'Nombre del grupo',
    instruction:
      'Agrupe los equipos en un mismo grupo. Puede organizarlos según las etapas del proceso de producción o agrupando los equipos principales junto con los equipos auxiliares.',
    exampleOne: 'Maquinaria de la línea de ensamblaje',
    exampleTwo: 'Equipos de comunicación',
  },

  [EQUIPMENT_SECTION_NAMES.NUMBER]: {
    name: 'Número de elementos del grupo',
    instruction: 'Indique el número total de elementos que componen el grupo de equipos.',
    exampleOne: '11',
    exampleTwo: '6',
  },

  [EQUIPMENT_SECTION_NAMES.COST]: {
    name: 'Valor actual',
    instruction: 'Indique el valor total actual del grupo de equipos.',
    exampleOne: '850 000 USD',
    exampleTwo: '60 000 USD',
  },

  [EQUIPMENT_SECTION_NAMES.PURPOSE]: {
    name: 'Finalidad',
    instruction:
      'Describa la función o el propósito del grupo de equipos. Explique los resultados que permiten alcanzar o el papel que desempeñan dentro de los procesos operativos.',
    exampleOne:
      'Está diseñado para automatizar el proceso de ensamblaje de componentes y piezas, aumentando la productividad y reduciendo los costes laborales. Su función consiste en garantizar un ensamblaje fluido y secuencial de los productos.',
    exampleTwo:
      'Facilita la interacción fluida entre los miembros del equipo, los clientes y las partes interesadas, permitiendo la colaboración en tiempo real y mejorando el servicio al cliente.',
  },
};

const PURCHASED_EQUIPMENT_INSTRUCTIONS = {
  [EQUIPMENT_SECTION_NAMES.FILE]: {
    name: '',
    instruction:
      'Incluya una fotografía de alta calidad del equipo. Asegúrese de que la imagen esté bien iluminada y tomada de manera profesional.',
    exampleOne: '',
    exampleTwo: '',
  },

  [EQUIPMENT_SECTION_NAMES.NAME]: {
    name: 'Nombre del grupo',
    instruction: 'Indique el nombre del equipo o del grupo de equipos cuya adquisición está prevista.',
    exampleOne: 'Equipos de manipulación de materiales',
    exampleTwo: 'Servidores de bases de datos',
  },

  [EQUIPMENT_SECTION_NAMES.NUMBER]: {
    name: 'Número de elementos',
    instruction: 'Indique el número total de elementos del grupo de equipos.',
    exampleOne: '13',
    exampleTwo: '20',
  },

  [EQUIPMENT_SECTION_NAMES.COST]: {
    name: 'Coste previsto',
    instruction: 'Indique el valor total previsto del grupo de equipos.',
    exampleOne: '8 millones de USD',
    exampleTwo: '80 000 USD',
  },

  [EQUIPMENT_SECTION_NAMES.PURPOSE]: {
    name: 'Finalidad',
    instruction:
      'Describa la función o el propósito del nuevo equipo. Explique qué resultados permitirá obtener o cuál será su papel dentro de los procesos operativos.',
    exampleOne:
      'Mejora la eficiencia operativa al reducir el trabajo manual. Además, garantiza un flujo de trabajo continuo y la entrega puntual de componentes, contribuyendo al aumento de la productividad y la rentabilidad.',
    exampleTwo:
      'Proporcionan un repositorio centralizado para almacenar información crítica, garantizando la integridad, la seguridad y la accesibilidad de los datos.',
  },
};

const EQUIPMENT_INSTRUCTIONS = {
  [EQUIPMENT_NAMES.DESCRIPTION]: {
    name: 'Uso del equipo',
    instruction:
      'Proporcione una descripción concisa del equipo que utiliza actualmente su empresa. Incluya maquinaria, herramientas, tecnologías y otros activos esenciales para sus operaciones. Indique claramente los tipos, modelos y capacidades de los equipos para ofrecer una visión general de la infraestructura que impulsa sus procesos empresariales.',
    exampleOne:
      'La línea de producción de la empresa está equipada con maquinaria de última generación, incluidos centros de mecanizado CNC, estaciones robotizadas de soldadura y herramientas de ensamblaje de alta precisión. Estas instalaciones permiten fabricar componentes especializados y ensamblar tractores de orugas con tecnología patentada de orugas continuas, garantizando una producción eficiente y de máxima calidad.',
    exampleTwo:
      'El equipamiento de la empresa incluye software de traducción de última generación, como herramientas CAT (SDL Trados y memoQ), respaldado por servidores de alta velocidad para el procesamiento de datos. Además, utiliza algoritmos avanzados de inteligencia artificial para el control de calidad, mejorando la precisión y la eficiencia de las traducciones.',
  },

  [EQUIPMENT_NAMES.MAIN_EQUIPMENT]: MAIN_EQUIPMENT_INSTRUCTIONS,

  [EQUIPMENT_NAMES.LEVEL_OF_UTILIZATION]: {
    name: 'Nivel de utilización',
    instruction:
      'Describa el nivel de utilización de los equipos, centrándose en la eficiencia y la optimización. Incluya información sobre los patrones de uso, las estrategias para reducir el tiempo de inactividad y los programas de mantenimiento.',
    exampleOne:
      'Los equipos de la empresa operan con un nivel máximo de eficiencia y mantienen de forma constante una tasa de utilización superior al 90 %. La empresa aplica programas de mantenimiento preventivo para minimizar el tiempo de inactividad y optimizar el rendimiento. La supervisión continua y el análisis de datos impulsan mejoras permanentes de la eficiencia y garantizan procesos de producción estables.',
    exampleTwo:
      'Los equipos de la empresa funcionan con una eficiencia óptima y mantienen una tasa media de utilización del 90 %. El tiempo de inactividad se reduce mediante programas proactivos de mantenimiento respaldados por análisis predictivos. Los patrones de uso se analizan continuamente para optimizar el rendimiento, maximizar la productividad y minimizar las interrupciones.',
  },

  [EQUIPMENT_NAMES.PURCHASED_EQUIPMENT]: PURCHASED_EQUIPMENT_INSTRUCTIONS,

  [EQUIPMENT_NAMES.RATIONALE_FOR_PURCHASING]: {
    name: 'Justificación de la adquisición',
    instruction:
      'Justifique la adquisición del equipo destacando las mejoras en eficiencia y el ahorro de costes. Explique cómo el equipo responde a las necesidades de producción y contribuye al logro de los objetivos de crecimiento de la empresa.',
    exampleOne:
      'La empresa invierte en equipos de última generación para mejorar la eficiencia y reducir los costes a largo plazo. La modernización garantiza la adaptación a las necesidades cambiantes de la producción, aumentando el volumen de fabricación y la calidad. Esta inversión estratégica acelera el crecimiento de la empresa y refuerza su competitividad en el mercado.',
    exampleTwo:
      'La inversión en software de traducción de última generación y algoritmos de inteligencia artificial está alineada con el objetivo de la empresa de aumentar la eficiencia y reducir los costes operativos. Estas herramientas optimizan los procesos, permiten entregar traducciones de alta calidad en menor tiempo y facilitan la escalabilidad necesaria para responder al crecimiento de la demanda.',
  },
};
const GROUPS_INSTRUCTIONS = {
  [EQUIPMENT_SECTION_NAMES.FILE]: {
    name: '',
    instruction:
      'Incluya una fotografía de alta calidad del activo. Asegúrese de que la imagen esté bien iluminada y tomada de manera profesional.',
    exampleOne: '',
    exampleTwo: '',
  },

  [EQUIPMENT_SECTION_NAMES.NAME]: {
    name: 'Nombre del grupo',
    instruction: 'Indique el nombre específico del grupo de activos.',
    exampleOne: 'Equipos de servicio',
    exampleTwo: 'Bases de datos lingüísticas',
  },

  [EQUIPMENT_SECTION_NAMES.NUMBER]: {
    name: 'Número de elementos',
    instruction: 'Indique el número total de elementos del grupo de activos.',
    exampleOne: '9',
    exampleTwo: '3',
  },

  [EQUIPMENT_SECTION_NAMES.COST]: {
    name: 'Valor actual',
    instruction: 'Indique el valor total actual del grupo de activos.',
    exampleOne: '1 500 000 $',
    exampleTwo: '300 000 $',
  },

  [EQUIPMENT_SECTION_NAMES.PURPOSE]: {
    name: 'Finalidad',
    instruction:
      'Explique cómo el grupo de activos contribuye a las operaciones, al crecimiento o a los objetivos de la empresa. Describa su importancia y los beneficios que aporta, incluyendo su impacto en la productividad, la eficiencia, la generación de beneficios y la estrategia de crecimiento.',
    exampleOne:
      'Estos activos permiten mantener y reparar los tractores de la empresa de forma eficiente. Facilitan intervenciones oportunas, prolongan la vida útil de los tractores y garantizan un rendimiento óptimo, lo que incrementa la satisfacción y la fidelidad de los clientes.',
    exampleTwo:
      'Estas bases de datos almacenan información lingüística, incluida terminología y expresiones necesarias para realizar traducciones y procesos de localización con precisión. Los especialistas lingüísticos las utilizan para superar las barreras idiomáticas entre culturas e idiomas, garantizando una comunicación clara y culturalmente adecuada.',
  },
};

const PURCHASED_GROUPS_INSTRUCTIONS = {
  [EQUIPMENT_SECTION_NAMES.FILE]: {
    name: '',
    instruction:
      'Incluya una fotografía de alta calidad del activo. Asegúrese de que la imagen esté bien iluminada y tomada de manera profesional.',
    exampleOne: '',
    exampleTwo: '',
  },

  [EQUIPMENT_SECTION_NAMES.NAME]: {
    name: 'Nombre del grupo',
    instruction: 'Indique el nombre específico del grupo de activos.',
    exampleOne: 'Sistemas de imágenes satelitales',
    exampleTwo: 'Herramientas de traducción basadas en IA',
  },

  [EQUIPMENT_SECTION_NAMES.NUMBER]: {
    name: 'Número de elementos',
    instruction: 'Indique el número total de elementos del grupo de activos.',
    exampleOne: '3',
    exampleTwo: '2',
  },

  [EQUIPMENT_SECTION_NAMES.COST]: {
    name: 'Coste previsto',
    instruction: 'Indique el valor total previsto del grupo de activos.',
    exampleOne: '1 000 000 $',
    exampleTwo: '100 000 $',
  },

  [EQUIPMENT_SECTION_NAMES.PURPOSE]: {
    name: 'Finalidad',
    instruction:
      'Explique el propósito de la adquisición prevista e indique qué tareas u objetivos se alcanzarán gracias a estos activos.',
    exampleOne:
      'Proporcionan información en tiempo real sobre el estado de los campos, permitiendo supervisar con precisión la salud de los cultivos, los niveles de humedad del suelo y la gestión integral de las tierras para aumentar la productividad y la eficiencia en el uso de los recursos.',
    exampleTwo:
      'La plataforma permite una comunicación más rápida y fiable entre clientes y traductores en múltiples idiomas, mejorando la eficiencia y la precisión de los procesos de traducción.',
  },
};

const ASSETS_INSTRUCTIONS = {
  [ASSETS_NAMES.DESCRIPTION]: {
    name: 'Activos no corrientes',
    instruction:
      'Identifique los principales activos no corrientes, como terrenos, activos biológicos, medios de transporte y activos intangibles. Proporcione una breve descripción de cada categoría para ofrecer una visión completa.',
    exampleOne: 'Camiones. Cinco camiones garantizan el suministro de piezas y materias primas necesarias.',
    exampleTwo:
      'Vehículos de empresa. Los directivos utilizan estos vehículos para desplazarse a reuniones con proveedores, inversores o socios.',
  },

  [ASSETS_NAMES.GROUPS]: GROUPS_INSTRUCTIONS,

  [ASSETS_NAMES.PURCHASED_GROUPS]: PURCHASED_GROUPS_INSTRUCTIONS,

  [ASSETS_NAMES.RATIONALE_FOR_PURCHASING]: {
    name: 'Justificación de la adquisición',
    instruction:
      'Explique la importancia estratégica de la adquisición de activos, destacando el valor añadido y los beneficios operativos. Describa cómo estos activos contribuyen a mejorar la eficiencia, la productividad o la ventaja competitiva de la empresa.',
    exampleOne:
      'La adquisición de activos adicionales, como software avanzado, mejora el análisis de datos y optimiza las operaciones, aumentando la eficiencia y facilitando una mejor toma de decisiones. Estas inversiones incrementan la productividad y fortalecen la posición competitiva de la empresa, impulsando un crecimiento sostenible y la innovación.',
    exampleTwo:
      'La adquisición de activos adicionales, como servidores de alta velocidad y sistemas avanzados de seguridad de datos, mejora la eficiencia operativa y refuerza la ventaja competitiva de la empresa. Estas inversiones aumentan la capacidad de procesamiento de datos, garantizan operaciones ininterrumpidas y protegen la información sensible, impulsando el crecimiento del negocio.',
  },
};

const STAGES_INSTRUCTIONS = {
  [STAGES_NAMES.STAGE]: {
    name: 'Etapas del proceso',
    instruction:
      'Describa las principales etapas del proceso de producción. Presente una visión paso a paso de la creación del producto o la prestación del servicio. Explique brevemente cada etapa para ofrecer una comprensión clara del proceso tecnológico.',
    exampleOne:
      'Fabricación de componentes: producción de piezas individuales como chasis, componentes del motor, sistemas de transmisión y conjuntos de orugas.',
    exampleTwo:
      'Envío de la solicitud: los clientes envían solicitudes de traducción indicando los idiomas, los plazos y los detalles del proyecto.',
  },
};

const TECHNOLOGICAL_PROCESS_INSTRUCTIONS = {
  [TECHNOLOGICAL_PROCESS_NAMES.TECHNOLOGY]: {
    name: 'Tecnología de producción',
    instruction:
      'Describa la tecnología utilizada actualmente en la producción o en la prestación de servicios. Destaque las mejoras en eficiencia, calidad y ventajas competitivas que proporciona.',
    exampleOne:
      'La tecnología de producción actual utiliza una tecnología patentada de nanocaucho. Esta innovación, aplicada al diseño de orugas continuas de la empresa, reduce la presión sobre el suelo y su impacto, aumentando la eficiencia agrícola y proporcionando una ventaja competitiva.',
    exampleTwo:
      'La prestación del servicio utiliza software de traducción de última generación impulsado por algoritmos de inteligencia artificial, lo que garantiza una precisión y una eficiencia excepcionales. Esta tecnología permite ofrecer traducciones de alta calidad en plazos reducidos, reforzando la competitividad de la empresa y aumentando la satisfacción de los clientes.',
  },

  [TECHNOLOGICAL_PROCESS_NAMES.QUALITIES]: {
    name: 'Ventajas y desventajas',
    instruction:
      'Describa las ventajas de la tecnología actual, centrándose en la eficiencia, la calidad y la ventaja competitiva. Señale también sus limitaciones, como los costes, la curva de aprendizaje o la dependencia de determinadas infraestructuras.',
    exampleOne:
      'La tecnología proporciona una mayor tracción, reduce la presión sobre el suelo, mejora la flotación y aumenta la eficiencia agrícola. Sin embargo, implica mayores costes de producción, una menor resistencia al desgaste y un ámbito de aplicación limitado fuera del sector agrícola.',
    exampleTwo: '',
  },

  [TECHNOLOGICAL_PROCESS_NAMES.STAGES]: STAGES_INSTRUCTIONS,

  [TECHNOLOGICAL_PROCESS_NAMES.COMPETING_TECHNOLOGY]: {
    name: 'Tecnologías competidoras',
    instruction:
      'Presente una visión general del panorama tecnológico de su sector. Identifique las principales tecnologías utilizadas actualmente por sus competidores o que estén emergiendo en el mercado. Explique cómo estas tecnologías pueden influir en su producto o servicio y analice sus fortalezas y debilidades.',
    exampleOne:
      'Las tecnologías competidoras, como los vehículos autónomos y los tractores eléctricos, representan una posible amenaza. Aunque ofrecen avances en eficiencia y sostenibilidad, pueden desafiar el liderazgo de la empresa en el mercado tradicional de tractores debido a sus elevados costes iniciales y a las barreras para su adopción.',
    exampleTwo:
      'Tecnologías como la traducción automática, las API de procesamiento del lenguaje y las plataformas colaborativas pueden afectar a la cuota de mercado de la empresa. Aunque ofrecen automatización y escalabilidad, todavía pueden carecer de la precisión y del control de calidad que aportan los traductores humanos.',
  },
};
const WORKING_RESOURCES_INSTRUCTIONS = {
  [WORKING_RESOURCES_NAMES.NECESSARY_RECOURSES]: {
    name: 'Recursos materiales',
    instruction:
      'Presente una descripción concisa de los recursos esenciales necesarios para su proceso de producción. Defina las principales categorías de recursos, como materias primas, materiales y componentes. Explique el papel fundamental que desempeñan estos recursos en el ciclo de producción y destaque su importancia para garantizar un proceso de fabricación continuo y eficiente.',
    exampleOne:
      'La empresa necesita caucho, plásticos y acero para la producción. También utiliza componentes como orugas, chasis, motores, transmisiones, cilindros hidráulicos, carrocerías, sistemas de refrigeración, puestos de conducción, combustible y sistemas eléctricos.',
    exampleTwo:
      'Los recursos materiales necesarios incluyen suministros de oficina como material de papelería, café, té, agua y vajilla desechable. Estos elementos son esenciales para mantener un entorno de trabajo cómodo para los empleados.',
  },

  [WORKING_RESOURCES_NAMES.AVAILABILITY]: {
    name: 'Estabilidad del suministro',
    instruction:
      'Describa la disponibilidad y la estabilidad del suministro de recursos, destacando su fiabilidad y continuidad. Explique los planes de gestión de inventario previstos para hacer frente a posibles interrupciones y garantizar la continuidad de las operaciones.',
    exampleOne:
      'El suministro de recursos se supervisa continuamente para garantizar su estabilidad y disponibilidad. La empresa mantiene una red diversificada de proveedores para minimizar el riesgo de interrupciones. La estrategia de inventario incluye existencias de seguridad y fuentes alternativas de suministro para asegurar la continuidad de las operaciones ante posibles incidencias.',
    exampleTwo:
      'Una red de proveedores fiables y una estrategia de abastecimiento diversificada garantizan la disponibilidad y estabilidad de los recursos. El proveedor principal ha demostrado su fiabilidad durante los últimos cuatro años. Además, se realiza un seguimiento constante del mercado para reaccionar rápidamente ante cualquier circunstancia imprevista.',
  },
};

const KEY_SUPPLIERS_INSTRUCTIONS = {
  [KEY_SUPPLIERS_CHARACTERIZE.NAME]: {
    name: 'Nombre del proveedor',
    instruction: 'Indique el nombre completo del proveedor e incluya cualquier información relevante.',
    exampleOne: 'Steelnow',
    exampleTwo: 'VPS',
  },

  [KEY_SUPPLIERS_CHARACTERIZE.PRODUCT]: {
    name: 'Producto o servicio',
    instruction: 'Indique el nombre del producto o servicio suministrado.',
    exampleOne: 'Acero de alta resistencia y baja aleación',
    exampleTwo:
      'Material de oficina: instrumentos de escritura, papel, blocs, archivadores y material de almacenamiento',
  },

  [KEY_SUPPLIERS_CHARACTERIZE.QUANTITY]: {
    name: 'Cantidad mensual',
    instruction:
      'Indique la cantidad mensual del producto o servicio suministrado, especificando las unidades correspondientes.',
    exampleOne: '1,5 t',
    exampleTwo: '50 unidades en total',
  },

  [KEY_SUPPLIERS_CHARACTERIZE.PRICE]: {
    name: 'Precio',
    instruction: 'Indique con precisión el precio del producto o servicio suministrado.',
    exampleOne: '500 USD por tonelada',
    exampleTwo: '300 USD',
  },

  [KEY_SUPPLIERS_CHARACTERIZE.DELIVERY_TERMS]: {
    name: 'Condiciones de pago',
    instruction:
      'Indique de forma concisa las condiciones de pago, especificando si se realiza mediante prepago, pago contra entrega o pago diferido.',
    exampleOne: '',
    exampleTwo: '',
  },
};

const PARTNERS_INSTRUCTIONS = {
  [PARTNERS_NAMES.KEY_SUPPLIERS]: KEY_SUPPLIERS_INSTRUCTIONS,

  [PARTNERS_NAMES.CRITERIA]: {
    name: 'Criterios de selección',
    instruction:
      'Describa los criterios y consideraciones que guiarán el proceso de selección de proveedores. Explique aspectos como precios competitivos, una logística eficiente, condiciones de financiación flexibles y la calidad de las materias primas. Destaque la importancia de cada criterio dentro de la estrategia y los objetivos de la empresa.',
    exampleOne:
      'Al seleccionar un proveedor, la empresa prioriza la calidad de las materias primas y los componentes, ya que es un factor decisivo para garantizar la durabilidad de los tractores. Asimismo, una logística fiable asegura la entrega puntual de materiales y componentes, un aspecto esencial para mantener la continuidad de los procesos de producción.',
    exampleTwo:
      'Al seleccionar un proveedor, la empresa prioriza precios competitivos para optimizar los costes, un sistema de suministro fiable que garantice la continuidad operativa y condiciones de financiación flexibles que contribuyan a la estabilidad financiera.',
  },
};
const PRODUCTION_INSTRUCTIONS = {
  [PRODUCTION_NAMES.SPECIFICS]: {
    name: 'Características de la producción',
    instruction:
      'Describa la organización del trabajo durante el año, teniendo en cuenta las fluctuaciones estacionales de la demanda. Explique cómo la empresa optimiza la producción en los períodos de mayor actividad, cómo adapta los horarios de trabajo y qué factores regulatorios o externos pueden afectar al calendario de producción.',
    exampleOne:
      'La producción se desarrolla durante todo el año. En las temporadas de mayor demanda, como la primavera y el otoño, la empresa amplía la jornada laboral e incorpora turnos adicionales para satisfacer el incremento de pedidos. Los horarios se ajustan de forma flexible para responder a las variaciones de la demanda.',
    exampleTwo:
      'La actividad de la empresa se adapta a la demanda estacional, cuyos picos coinciden con los períodos académicos y los eventos internacionales. Se aplican horarios de trabajo flexibles para responder eficazmente a estas fluctuaciones.',
  },

  [PRODUCTION_NAMES.PACKAGE]: {
    name: 'Embalaje',
    instruction:
      'Describa la estrategia de embalaje, destacando la protección del producto y la imagen de la marca. Explique los materiales utilizados, el diseño y, cuando corresponda, las iniciativas de sostenibilidad.',
    exampleOne:
      'Dependiendo del método de transporte, los tractores se cargan sobre plataformas o en contenedores. Se aseguran mediante correas o cadenas para evitar desplazamientos durante el transporte. Además, se utilizan materiales de protección para evitar daños en los componentes más sensibles.',
    exampleTwo:
      'La estrategia de embalaje de la empresa está orientada a garantizar la máxima protección del producto. Para reducir el impacto ambiental se emplean materiales resistentes y respetuosos con el medio ambiente. El diseño incorpora los colores y el logotipo corporativos para reforzar el reconocimiento de la marca.',
  },

  [PRODUCTION_NAMES.INVENTORY_MANAGEMENT]: {
    name: 'Gestión de inventario',
    instruction:
      'Presente una descripción general del sistema de gestión de inventario de la empresa. Indique el software o la plataforma utilizada para controlar el inventario y describa sus principales funciones, como el seguimiento en tiempo real, la gestión de pedidos y la integración con otros procesos empresariales.',
    exampleOne:
      'La empresa utiliza un sistema ERP para la gestión integral del inventario, que permite el seguimiento de pedidos en tiempo real y su integración con otros procesos empresariales, como la producción y las ventas. El sistema mantiene niveles óptimos de inventario para satisfacer la demanda y minimizar los costes de almacenamiento.',
    exampleTwo:
      'La empresa utiliza un sistema de gestión de inventario basado en la nube que permite el seguimiento en tiempo real y una integración fluida con otros servicios, garantizando un funcionamiento continuo y eficiente.',
  },

  [PRODUCTION_NAMES.OUTSOURCING_OPPORTUNITIES]: {
    name: 'Opciones de externalización',
    instruction:
      'Presente una visión general de las oportunidades de externalización disponibles para sus procesos de producción. Explique qué etapas o actividades pueden delegarse en terceros, los beneficios que ello aporta y las razones estratégicas que justifican esta decisión, como la reducción de costes, la escalabilidad o el acceso a conocimientos especializados.',
    exampleOne:
      'La empresa externaliza la fabricación de componentes, determinadas tareas de ensamblaje y la logística de distribución. Esta estrategia permite reducir costes, aumentar la escalabilidad y acceder a conocimientos técnicos especializados.',
    exampleTwo:
      'La empresa externaliza la atención al cliente y la creación de contenidos, lo que reduce la carga del departamento de recursos humanos y permite ejecutar proyectos sin necesidad de incorporar nuevos empleados a la plantilla.',
  },

  [PRODUCTION_NAMES.INVOLVED_PRODUCTION]: {
    name: 'Participación de terceros',
    instruction:
      'Indique qué etapas intermedias o actividades específicas de la producción se confían a terceros. Explique brevemente las razones de esta decisión, destacando las ventajas estratégicas y las eficiencias obtenidas gracias a la colaboración con socios externos.',
    exampleOne:
      'La colaboración con empresas especializadas es necesaria para fabricar componentes específicos, como sistemas hidráulicos y motores.',
    exampleTwo:
      'Se recurre a servicios de terceros para garantizar la precisión lingüística y la adaptación cultural durante la prestación del servicio. Esta colaboración mejora la calidad, fomenta la diversidad y acelera la ejecución de los proyectos.',
  },

  [PRODUCTION_NAMES.DISPOSAL_WAIST]: {
    name: 'Gestión de residuos',
    instruction:
      'Describa los procedimientos de gestión y eliminación de residuos, haciendo hincapié en la sostenibilidad y el cumplimiento normativo. Explique los métodos de clasificación, las iniciativas de reciclaje y la colaboración con empresas especializadas en la gestión de residuos.',
    exampleOne:
      'Los residuos se clasifican en las instalaciones para su reciclaje o eliminación segura. Materiales reciclables como metal, plástico y papel se separan del resto de residuos, mientras que los materiales peligrosos son gestionados por empresas certificadas. La empresa garantiza el cumplimiento normativo y la sostenibilidad mediante un seguimiento continuo de estos procesos.',
    exampleTwo:
      'Los residuos de equipos electrónicos se reciclan conforme a las normas internacionales, garantizando la sostenibilidad y el cumplimiento de la normativa. La empresa colabora con gestores especializados para asegurar una correcta clasificación, reciclaje y reducción del impacto ambiental.',
  },
};
const QUALITY_INSTRUCTIONS = {
  [QUALITY_NAMES.FACTORS]: {
    name: 'Factores de calidad',
    instruction:
      'Identifique y describa los factores clave que influyen en la calidad del producto final o del servicio prestado. Estos pueden incluir la calidad de las materias primas, los procesos de fabricación, la precisión de los equipos y el cumplimiento de las normas y estándares del sector.',
    exampleOne:
      'Los principales factores que influyen en la calidad del producto son la calidad de las materias primas y la precisión del ensamblaje. Las materias primas de alta calidad garantizan la durabilidad del producto, mientras que un ensamblaje preciso asegura su fiabilidad y una larga vida útil.',
    exampleTwo:
      'Los principales factores que influyen en la calidad del servicio incluyen la precisión de la traducción, la adaptación cultural, el cumplimiento de las especificaciones del cliente y una comunicación eficaz entre traductores y clientes. Los procedimientos de aseguramiento de la calidad y los mecanismos de retroalimentación garantizan resultados consistentes y de alta calidad.',
  },

  [QUALITY_NAMES.METHODS]: {
    name: 'Métodos de control',
    instruction:
      'Describa los principales métodos de control de calidad aplicados durante la fabricación o la prestación de servicios. Explique los procedimientos de inspección, las pruebas y los puntos de control integrados en el proceso de producción para garantizar la consistencia y la fiabilidad del resultado final.',
    exampleOne:
      'Los métodos de control de calidad incluyen una inspección rigurosa de las materias primas, pruebas periódicas de los componentes y controles exhaustivos durante el ensamblaje. En cada etapa se verifican la precisión dimensional, la resistencia y el rendimiento para garantizar la consistencia y la fiabilidad del producto final.',
    exampleTwo:
      'Los principales métodos de control de calidad incluyen una revisión exhaustiva de los documentos, el uso de herramientas de traducción automatizada y la corrección realizada por lingüistas profesionales. Las auditorías periódicas, los mecanismos de retroalimentación de los clientes y las evaluaciones del rendimiento garantizan estándares de calidad uniformes durante toda la prestación del servicio.',
  },
};

export const BUSINESS_OPERATIONS_INSTRUCTIONS = {
  [BUSINESS_TOOL_SUB_SECTIONS.BUSINESS_OPERATIONS.LOCATION_AND_PREMISES]: LOCATION_AND_PREMISES_INSTRUCTIONS,
  [BUSINESS_TOOL_SUB_SECTIONS.BUSINESS_OPERATIONS.EQUIPMENT]: EQUIPMENT_INSTRUCTIONS,
  [BUSINESS_TOOL_SUB_SECTIONS.BUSINESS_OPERATIONS.ASSETS]: ASSETS_INSTRUCTIONS,
  [BUSINESS_TOOL_SUB_SECTIONS.BUSINESS_OPERATIONS.TECHNOLOGICAL_PROCESS]: TECHNOLOGICAL_PROCESS_INSTRUCTIONS,
  [BUSINESS_TOOL_SUB_SECTIONS.BUSINESS_OPERATIONS.WORKING_RESOURCES]: WORKING_RESOURCES_INSTRUCTIONS,
  [BUSINESS_TOOL_SUB_SECTIONS.BUSINESS_OPERATIONS.PARTNERS]: PARTNERS_INSTRUCTIONS,
  [BUSINESS_TOOL_SUB_SECTIONS.BUSINESS_OPERATIONS.PRODUCTION]: PRODUCTION_INSTRUCTIONS,
  [BUSINESS_TOOL_SUB_SECTIONS.BUSINESS_OPERATIONS.QUALITY]: QUALITY_INSTRUCTIONS,
};
